import { hostedOnboardingD2pStatusOptions, hostedOnboardingD2pStatusPostMutation } from '@onefootprint/fetch-hosted';
import type { D2pSessionStatus } from '@onefootprint/request-types';
import { useMutation, useQuery } from '@tanstack/react-query';
import { useCallback, useEffect, useRef } from 'react';
import { getLogger } from '@/idv2/utils/logger';
const {
  logInfo,
  logError
} = getLogger({
  name: 'idv2',
  location: 'use-d2p-status-poll'
});
const D2P_POLL_INTERVAL = 1000;
const TERMINAL_STATUSES: D2pSessionStatus[] = ['completed', 'failed', 'canceled'];
export const isTerminalStatus = (status: D2pSessionStatus | undefined): boolean => !!status && TERMINAL_STATUSES.includes(status);
type UseD2pStatusPollParams = {
  authToken: string;
  enabled?: boolean;
  onStatusChange?: (status: D2pSessionStatus) => void;
};
const useD2pStatusPoll = ({
  authToken,
  enabled = true,
  onStatusChange
}: UseD2pStatusPollParams) => {
  const prevStatusRef = useRef<D2pSessionStatus | undefined>(undefined);
  const hasFiredTerminalRef = useRef(false);
  const onStatusChangeRef = useRef(onStatusChange);
  onStatusChangeRef.current = onStatusChange;
  const statusQuery = useQuery({
    ...hostedOnboardingD2pStatusOptions({
      headers: {
        'X-Fp-Authorization': authToken
      }
    }),
    enabled: enabled && !!authToken,
    refetchInterval: query => isTerminalStatus(query.state.data?.status) ? false : D2P_POLL_INTERVAL,
    // The handoff writes the terminal status while the opener sits hidden behind it. The interval
    // would otherwise pause the moment focus leaves, and bifrost turns the focus refetch off for
    // every query, so that status is never read and the transfer screen dead-ends.
    refetchIntervalInBackground: true,
    refetchOnWindowFocus: true
  });
  const status = statusQuery.data?.status;
  useEffect(() => {
    if (!status || status === prevStatusRef.current) return;
    prevStatusRef.current = status;
    if (hasFiredTerminalRef.current) return;
    if (isTerminalStatus(status)) {
      hasFiredTerminalRef.current = true;
    }
    onStatusChangeRef.current?.(status);
  }, [status]);
  const updateMutation = useMutation(hostedOnboardingD2pStatusPostMutation());
  const updateStatus = useCallback((newStatus: D2pSessionStatus) => {
    if (!authToken) return;
    updateMutation.mutate({
      headers: {
        'X-Fp-Authorization': authToken
      },
      body: {
        status: newStatus
      }
    });
  }, [authToken]);
  const cancel = useCallback(async () => {
    if (!authToken || updateMutation.isPending) return;
    try {
      await updateMutation.mutateAsync({
        headers: {
          'X-Fp-Authorization': authToken
        },
        body: {
          status: 'canceled'
        }
      });
      logInfo('D2P session cancel request sent');
    } catch (error) {
      logError('Failed to cancel D2P session', error);
    }
  }, [authToken, updateMutation]);
  return {
    status,
    isTerminal: isTerminalStatus(status),
    isPolling: !!authToken && enabled && !isTerminalStatus(status),
    isCanceling: updateMutation.isPending,
    error: statusQuery.error,
    cancel,
    updateStatus
  };
};
export default useD2pStatusPoll;